/**
 * Keeps the MNML Volume Pricing widget's profit figures in step with the prices it renders.
 *
 * The app stores each tier's badge as fixed text, so it can't follow a percentage tier or a
 * variant price. This reads the per-unit price the app puts on every tier row, works out the
 * profit against the variant's retail price, and rewrites the badges. It re-runs whenever the
 * app re-renders the widget, which is what happens on a variant change.
 */
class WholesaleProfit extends HTMLElement {
  connectedCallback() {
    this.retailPrices = this.parseRetailPrices();
    this.currency = this.dataset.currency || 'USD';
    this.badgeTemplate = this.dataset.badgeTemplate || 'Est. [profit] profit';
    this.panel = this.querySelector('[data-wholesale-panel]');

    this.widget = this.findWidget();
    if (!this.widget) return;

    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this.widget, { childList: true, subtree: true, attributes: true });
    this.widget.addEventListener('change', () => this.update());

    this.update();

    if (this.dataset.preselectTopTier === 'true') this.watchTopTier();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.topTierObserver) this.topTierObserver.disconnect();
    clearTimeout(this.topTierTimer);
  }

  /**
   * Starts the widget on its highest quantity tier. The app picks the first tier when it
   * loads and again each time it re-renders for a new variant, so this runs after every
   * render. The tiers element is marked once the top tier sticks, and a tier the buyer
   * picks after that is left alone.
   */
  watchTopTier() {
    const root = this.closest('.shopify-section') || document.body;
    this.topTierObserver = new MutationObserver(() => this.queueTopTier());
    this.topTierObserver.observe(root, { childList: true, subtree: true });
    this.queueTopTier();
  }

  queueTopTier() {
    clearTimeout(this.topTierTimer);
    this.topTierTimer = setTimeout(() => this.selectTopTier(), 150);
  }

  selectTopTier() {
    const widget = this.findWidget();
    const container = widget && widget.querySelector('.mnml-volume-pricing__tiers');
    if (!container || container.dataset.topTierSelected) return;

    // A new tiers element means the app re-rendered, so start counting attempts again.
    if (container !== this.topTierContainer) {
      this.topTierContainer = container;
      this.topTierAttempts = 0;
    }
    if (this.topTierAttempts >= 40) return;

    const tiers = Array.from(container.querySelectorAll('.mnml-volume-pricing__tier'));
    const top = tiers.reduce((best, tier) => (this.tierQty(tier) > this.tierQty(best) ? tier : best), tiers[0]);
    if (!top || this.tierQty(top) <= 1) return;

    // Until the app's script has loaded, a click only ticks the radio and the app then
    // selects its first tier, so keep trying until the quantity follows the top tier.
    if (this.topTierApplied(top)) {
      container.dataset.topTierSelected = 'true';
      return;
    }
    (top.querySelector('.mnml-volume-pricing__radio') || top).click();

    this.topTierAttempts += 1;
    this.queueTopTier();
  }

  tierQty(tier) {
    return parseInt(tier.dataset.minQty || tier.dataset.qty, 10) || 0;
  }

  topTierApplied(tier) {
    const input = (this.closest('.shopify-section') || document).querySelector('input[name="quantity"]');
    return (
      tier.classList.contains('mnml-volume-pricing__tier--selected') &&
      (!input || parseInt(input.value, 10) === this.tierQty(tier))
    );
  }

  parseRetailPrices() {
    try {
      return JSON.parse(this.dataset.retailPrices || '{}');
    } catch (error) {
      return {};
    }
  }

  /** The app renders one widget per section; fall back to the first on the page. */
  findWidget() {
    const sectionId = this.dataset.sectionId;
    const scoped = document.querySelector(`.mnml-volume-pricing[data-section-id="${sectionId}"]`);
    return scoped || document.querySelector('.mnml-volume-pricing');
  }

  /** Retail price in cents for the variant the widget is currently showing. */
  retailFor(variantId) {
    const retail = this.retailPrices[variantId];
    if (retail) return retail;

    const base = parseInt(this.widget.dataset.basePrice, 10);
    return Number.isFinite(base) ? base * 2 : null;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.currency,
    }).format(cents / 100);
  }

  update() {
    const retail = this.retailFor(this.widget.dataset.variantId);
    if (!retail) return;

    const tiers = this.widget.querySelectorAll('.mnml-volume-pricing__tier');
    let selected = null;

    tiers.forEach((tier) => {
      // Cents, but percentage tiers carry fractions of a cent (e.g. "701.46").
      const perItem = Math.round(parseFloat(tier.dataset.pricePerItem));
      if (!Number.isFinite(perItem)) return;

      const profit = retail - perItem;
      const badge = tier.querySelector('.mnml-volume-pricing__badge');
      if (badge) {
        const text = this.badgeTemplate.replace('[profit]', this.formatMoney(profit));
        // Skip the write when nothing changed, so we don't retrigger our own observer.
        if (badge.textContent.trim() !== text) badge.textContent = text;
      }

      if (tier.classList.contains('mnml-volume-pricing__tier--selected')) {
        selected = { perItem, profit };
      }
    });

    this.updatePanel(retail, selected);
  }

  updatePanel(retail, selected) {
    if (!this.panel || !selected) return;

    const margin = retail > 0 ? Math.round((selected.profit / retail) * 100) : 0;

    this.setText('[data-wholesale-retail]', this.formatMoney(retail));
    this.setText('[data-wholesale-profit]', this.formatMoney(selected.profit));
    this.setText('[data-wholesale-margin]', `${margin}%`);
    this.panel.hidden = false;
  }

  setText(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }
}

if (!customElements.get('wholesale-profit')) {
  customElements.define('wholesale-profit', WholesaleProfit);
}
